import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Building2, ArrowRight, MoreHorizontal, Pencil, EyeOff, Eye, Trash2, UserCheck, X, AlertTriangle } from "lucide-react";
import { apiClient } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";

const CATEGORY_LABELS = {
  individual: { ar: "فرد", en: "Individual" },
  dealer: { ar: "معرض سيارات", en: "Dealer" },
  official_agency: { ar: "وكالة رسمية", en: "Agency" },
  insurance_company: { ar: "شركة تأمين", en: "Insurance" },
  insurance_broker: { ar: "وسيط تأمين", en: "Insurance Broker" },
  finance_company: { ar: "شركة تمويل", en: "Finance" },
  leasing_company: { ar: "شركة تأجير تمويلي", en: "Leasing" },
  bank: { ar: "بنك", en: "Bank" },
  fintech: { ar: "تقنية مالية", en: "FinTech" },
  bnpl_service: { ar: "الشراء الآن والدفع لاحقًا", en: "BNPL" },
  financing_platform: { ar: "منصة تمويل", en: "Financing Platform" },
  payment_provider: { ar: "مزود دفع", en: "Payment Provider" },
  rental_company: { ar: "تأجير سيارات", en: "Rental" },
  transport_company: { ar: "نقل مركبات", en: "Transport" },
  inspection_center: { ar: "مركز فحص", en: "Inspection" },
  periodic_inspection: { ar: "فحص دوري", en: "Periodic Inspection" },
  workshop: { ar: "ورشة", en: "Workshop" },
  parts_store: { ar: "قطع غيار", en: "Parts" },
  tire_battery_shop: { ar: "إطارات وبطاريات", en: "Tires & Batteries" },
  car_wash: { ar: "غسيل وتلميع", en: "Car Wash" },
  recovery_towing: { ar: "سطحة", en: "Recovery" },
  salvage: { ar: "سكراب", en: "Salvage" },
  auction_house: { ar: "دار مزادات", en: "Auction House" },
  other: { ar: "أخرى", en: "Other" },
};

export default function MyBusinessesPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter160 — activity management surface
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind, business}
  const [transferEmail, setTransferEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reload = () => {
    setLoading(true);
    return apiClient.get("/platform/businesses/mine")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) return;
    reload();
  }, [user]);

  // Close the action menu when clicking anywhere outside
  useEffect(() => {
    if (menuOpenFor == null) return;
    const off = () => setMenuOpenFor(null);
    document.addEventListener("click", off);
    return () => document.removeEventListener("click", off);
  }, [menuOpenFor]);

  const isOwnerOf = (b) => b?.owner_user_id === user?.id;

  const onToggleActive = async (b) => {
    setBusy(true); setError("");
    try {
      const url = b.is_active
        ? `/platform/businesses/${b.id}/disable`
        : `/platform/businesses/${b.id}/reactivate`;
      await apiClient.post(url);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail?.ar || e?.response?.data?.detail || "حدث خطأ");
    } finally { setBusy(false); }
  };

  const onConfirmDelete = async () => {
    if (!confirm?.business) return;
    setBusy(true); setError("");
    try {
      await apiClient.delete(`/platform/businesses/${confirm.business.id}`);
      setConfirm(null);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail?.ar || e?.response?.data?.detail || "حدث خطأ");
    } finally { setBusy(false); }
  };

  const onConfirmTransfer = async () => {
    if (!confirm?.business || !transferEmail.trim()) return;
    setBusy(true); setError("");
    try {
      await apiClient.post(
        `/platform/businesses/${confirm.business.id}/transfer`,
        { new_owner_email: transferEmail.trim() },
      );
      setConfirm(null);
      setTransferEmail("");
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError((typeof d === "string" ? d : d?.ar) || "حدث خطأ");
    } finally { setBusy(false); }
  };

  if (user === undefined) return null; // wait for auth to hydrate
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="my-businesses">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-semibold">
            {lang === "ar" ? "مساحات العمل" : "Workspaces"}
          </div>
          <h1 className="text-3xl font-display font-bold text-[#0a0a0a] mt-1">
            {lang === "ar" ? "أنشطتي التجارية" : "My Businesses"}
          </h1>
        </div>
        {/* iter218 — a business `member` may only OPEN activities they
            were invited into; they cannot start a brand-new business
            under their own account (that would create the parallel
            personal-owner track we forbid). The backend enforces the
            same rule with a 403; this hides the CTA so the button
            never appears on their screen. Empty-state CTA below is
            gated identically. */}
        {!(user?.account_type === "member" || user?.role === "business_member") && (
          <Link to="/businesses/new"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold shadow-sm"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                data-testid="btn-new-business">
            <Plus className="w-4 h-4" /> {lang === "ar" ? "تسجيل نشاط جديد" : "Register business"}
          </Link>
        )}
      </div>

      {loading ? (
        <div className="text-[#8a8a8a]">…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 p-12 text-center">
          <Building2 className="w-10 h-10 mx-auto text-[#c9a55c] mb-3" />
          {(user?.account_type === "member" || user?.role === "business_member") ? (
            <>
              <div className="text-lg font-semibold mb-1">
                {lang === "ar" ? "لم تُضَف بعد إلى أي نشاط" : "You have not been added to a business yet"}
              </div>
              <div className="text-sm text-[#8a8a8a] mb-2 max-w-md mx-auto">
                {lang === "ar"
                  ? "حسابك عضو منشأة. تواصل مع صاحب النشاط لإرسال دعوة الانضمام على بريدك."
                  : "Your account is a business member. Ask the business owner to send an invitation to your email."}
              </div>
            </>
          ) : (
            <>
              <div className="text-lg font-semibold mb-1">{lang === "ar" ? "لم تُسجّل أي نشاط بعد" : "No businesses yet"}</div>
              <div className="text-sm text-[#8a8a8a] mb-5 max-w-md mx-auto">
                {lang === "ar"
                  ? "سجّل نشاطك التجاري وابنِ مساحة عمل احترافية داخل كارتال — لوحة تحكم، فروع، موظفون، تحليلات، وأكثر."
                  : "Register your business and get a full workspace inside CARTAL — dashboard, branches, employees, analytics, and more."}
              </div>
              <Link to="/businesses/new"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold"
                    style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}>
                <Plus className="w-4 h-4" /> {lang === "ar" ? "ابدأ الآن" : "Get started"}
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((b) => {
            const owner = isOwnerOf(b);
            const disabled = !b.is_active;
            return (
              <div key={b.id}
                   className={`relative rounded-2xl bg-white border p-5 hover:shadow-md transition-all ${disabled ? "border-red-200 opacity-90" : "border-[#0a0a0a]/[0.06] hover:border-[#c9a55c]/40"}`}
                   data-testid={`biz-card-${b.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {b.profile?.logo_url ? (
                    <img src={b.profile.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-[#0a0a0a]"
                         style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}>
                      {(b.name_ar || "?").charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{b.name_ar}</div>
                    <div className="text-[11px] text-[#8a8a8a]">
                      {CATEGORY_LABELS[b.category]?.[lang] || b.category}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          background: b.verification_status === "verified" ? "rgba(201,165,92,0.15)" : "rgba(0,0,0,0.05)",
                          color: b.verification_status === "verified" ? "#8a6f2e" : "#525252",
                        }}>
                    {b.verification_status === "verified"
                      ? (lang === "ar" ? "موثّق ✓" : "Verified ✓")
                      : (lang === "ar" ? "غير موثّق" : "Unverified")}
                  </span>
                  {disabled && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                          data-testid={`biz-disabled-${b.id}`}>
                      {lang === "ar" ? "معطّل" : "Disabled"}
                    </span>
                  )}
                  {!owner && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                      {lang === "ar" ? "عضو" : "Member"}
                    </span>
                  )}
                </div>

                {/* iter174 — Dedicated "Open activity" pill at the bottom
                    of the card. Big 44px+ touch target, clearly separated
                    from the ⋯ menu that sits at the opposite corner. */}
                <Link to={`/business/${b.id}`}
                      className="mt-4 inline-flex w-full items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-[#0a0a0a] hover:bg-[#f6efdf] border border-[#c9a55c]/40 transition-colors"
                      data-testid={`biz-open-${b.id}`}>
                  {lang === "ar" ? "فتح النشاط" : "Open activity"}
                  <ArrowRight className="w-4 h-4 rtl:rotate-180 text-[#8a6f2e]" strokeWidth={2.4} />
                </Link>

                {/* Action menu — owner only. Placed at the top-END (left
                    in RTL) with a 40×40 touch area and a subtle gold
                    surround so it reads as a distinct control. */}
                {owner && (
                  <div className="absolute top-3 end-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f6efdf]/70 hover:bg-[#f6efdf] border border-[#c9a55c]/25 hover:border-[#c9a55c]/60 transition-colors"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuOpenFor(menuOpenFor === b.id ? null : b.id); }}
                      data-testid={`biz-actions-btn-${b.id}`}
                      aria-label={lang === "ar" ? "إجراءات النشاط" : "Activity actions"}>
                      <MoreHorizontal className="w-5 h-5 text-[#8a6f2e]" />
                    </button>
                    {menuOpenFor === b.id && (
                      <div className="absolute top-11 end-0 z-20 w-56 bg-white rounded-xl border border-[#0a0a0a]/[0.08] shadow-lg py-1"
                           data-testid={`biz-actions-menu-${b.id}`}>
                        <Link to={`/business/${b.id}?edit=1`}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#f6efdf]"
                              data-testid={`biz-action-edit-${b.id}`}>
                          <Pencil className="w-4 h-4" />
                          {lang === "ar" ? "تعديل النشاط" : "Edit activity"}
                        </Link>
                        <button
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#f6efdf] text-left"
                          disabled={busy}
                          onClick={() => { setMenuOpenFor(null); onToggleActive(b); }}
                          data-testid={`biz-action-toggle-${b.id}`}>
                          {disabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          {disabled
                            ? (lang === "ar" ? "إعادة تفعيل النشاط" : "Reactivate activity")
                            : (lang === "ar" ? "تعطيل النشاط" : "Disable activity")}
                        </button>
                        <button
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[#f6efdf] text-left"
                          onClick={() => { setMenuOpenFor(null); setError(""); setTransferEmail(""); setConfirm({ kind: "transfer", business: b }); }}
                          data-testid={`biz-action-transfer-${b.id}`}>
                          <UserCheck className="w-4 h-4" />
                          {lang === "ar" ? "نقل الملكية" : "Transfer ownership"}
                        </button>
                        <div className="h-px bg-[#0a0a0a]/[0.06] my-1" />
                        <button
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
                          onClick={() => { setMenuOpenFor(null); setError(""); setConfirm({ kind: "delete", business: b }); }}
                          data-testid={`biz-action-delete-${b.id}`}>
                          <Trash2 className="w-4 h-4" />
                          {lang === "ar" ? "حذف النشاط" : "Delete activity"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Confirmation modals ─────────────────────────────────────── */}
      {confirm?.kind === "delete" && (
        <ConfirmModal
          testId="confirm-delete-modal"
          lang={lang}
          icon={<AlertTriangle className="w-6 h-6 text-red-600" />}
          title={lang === "ar" ? "حذف النشاط نهائيًا" : "Delete activity permanently"}
          body={lang === "ar"
            ? `سيتم حذف "${confirm.business.name_ar}" وجميع بياناتها (الفروع، الموظفون، الإعلانات، المراسلات، التحليلات) نهائيًا. لن يتأثر حسابك الشخصي أو أنشطتك الأخرى. هذا الإجراء لا يمكن التراجع عنه.`
            : `"${confirm.business.name_ar}" and every piece of data attached to it (branches, employees, listings, messages, analytics) will be permanently removed. Your account and any other activities you own are unaffected. This cannot be undone.`}
          onCancel={() => setConfirm(null)}
          confirmLabel={lang === "ar" ? "نعم، احذف نهائيًا" : "Yes, delete permanently"}
          confirmVariant="danger"
          onConfirm={onConfirmDelete}
          busy={busy}
          error={error}
        />
      )}

      {confirm?.kind === "transfer" && (
        <ConfirmModal
          testId="confirm-transfer-modal"
          lang={lang}
          icon={<UserCheck className="w-6 h-6 text-[#8a6f2e]" />}
          title={lang === "ar" ? "نقل ملكية النشاط" : "Transfer activity ownership"}
          body={lang === "ar"
            ? `أدخل البريد الإلكتروني لحساب كارتال المسجَّل الذي ستُنقل إليه ملكية "${confirm.business.name_ar}". ستفقد صلاحية الإدارة فور اكتمال النقل، وستنتقل جميع بيانات النشاط للمالك الجديد.`
            : `Enter the email of the registered CARTAL account that will own "${confirm.business.name_ar}". You will lose management access immediately after transfer; all activity data moves to the new owner.`}
          extra={
            <input
              type="email"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              placeholder="new-owner@example.com"
              className="w-full mt-3 px-4 py-2.5 rounded-xl border border-[#0a0a0a]/[0.15] text-sm focus:outline-none focus:border-[#c9a55c]"
              data-testid="transfer-email-input"
              autoFocus
            />
          }
          onCancel={() => { setConfirm(null); setTransferEmail(""); }}
          confirmLabel={lang === "ar" ? "نقل الملكية" : "Transfer"}
          confirmVariant="primary"
          confirmDisabled={!transferEmail.trim()}
          onConfirm={onConfirmTransfer}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
}

// Local confirmation modal — kept in this file to satisfy the "no new
// files / no unrelated refactor" constraint for the activity management
// task.
function ConfirmModal({ testId, lang, icon, title, body, extra, onCancel, onConfirm, confirmLabel, confirmVariant, confirmDisabled, busy, error }) {
  const btnStyle = confirmVariant === "danger"
    ? { background: "#dc2626", color: "#fff" }
    : { background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid={testId}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#f6efdf] flex items-center justify-center">{icon}</div>
            <h2 className="text-lg font-bold text-[#0a0a0a]">{title}</h2>
          </div>
          <button onClick={onCancel} className="p-1 rounded-full hover:bg-[#0a0a0a]/[0.05]" data-testid="modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#525252] leading-relaxed">{body}</p>
        {extra}
        {error && <div className="mt-3 text-sm text-red-600" data-testid="modal-error">{error}</div>}
        <div className="flex items-center justify-end gap-2 mt-6">
          <button onClick={onCancel} disabled={busy}
                  className="px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#0a0a0a]/[0.04]"
                  data-testid="modal-cancel">
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button onClick={onConfirm} disabled={busy || confirmDisabled}
                  className="px-5 py-2 rounded-full text-sm font-bold disabled:opacity-60"
                  style={btnStyle}
                  data-testid="modal-confirm">
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
